/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/editInvoice.scss';
import {ChangeFunction, ChangeKeyFunction, Loader} from '@dvrd/dvr-controls';

import React, {FocusEventHandler, MouseEventHandler} from 'react';

import {useTranslation} from 'react-i18next';

import Invoice from '../../../models/invoiceModel';

import OrderItem from '../../../models/orderItemModel';

import {getCountryCode, getVatItemsByCountry} from '../../../util/countryUtil';

import {AccessKey, OrderItemErrors, VATValue} from '../../../util/interfaces';

import {DefaultVAT} from '../../../util/vatUtil';

import {BolmateButton, BolmateInput, BolmateNumberInput} from '../../controls';

import BolmateVATSelect from '../../controls/select/bolmateVATSelect';

import useRight from '../../misc/useRight';

import InvoiceData from '../data/invoiceData';

interface Props {
    onChangeInvoice: ChangeKeyFunction;
    onChangeItem: (item: OrderItem, key: string) => ChangeFunction;
    onSubmit: MouseEventHandler;
    onCancel: MouseEventHandler;
    onResetItemError: (id: string, key: keyof OrderItemErrors) => FocusEventHandler;
    onChangeVAT: (item: OrderItem) => ChangeFunction<VATValue>;
    invoice: Invoice;
    loading: boolean;
    itemErrors: Record<string, OrderItemErrors>;
}

export default function EditInvoice(props: Props) {
    const {t} = useTranslation();
    const [canEditItems] = useRight(AccessKey.INVOICE_EDIT_ITEMS);
    const {
        invoice,
        onChangeInvoice,
        loading,
        itemErrors,
        onResetItemError,
        onChangeItem,
        onSubmit,
        onCancel,
        onChangeVAT
    } = props;

    function getVatItems(countryCode: string): Array<VATValue> {
        const items: Array<VATValue> = [DefaultVAT(0)];
        return getVatItemsByCountry(countryCode, items);
    }

    function renderInvoice() {
        return <InvoiceData invoice={invoice} loading={loading} onChange={onChangeInvoice} />;
    }

    function renderItems() {
        const items = invoice.items.filter((item: OrderItem) => !item.isCancelled);
        return (
            <div className='order-items-container'>
                <label className='col-title'>{t('invoice.edit.invoiceLines', 'Factuurregels')}</label>
                <div className='order-items'>
                    <div className='row head'>
                        <label>{t('invoice.edit.col.ean', 'EAN')}</label>
                        <label>{t('invoice.edit.col.reference', 'REFERENTIE')}</label>
                        <label>{t('invoice.edit.col.name', 'NAAM')}</label>
                        <label>{t('invoice.edit.col.price', 'PRIJS')}</label>
                        <label>{t('invoice.edit.col.quantity', 'AANTAL')}</label>
                        <label>{t('invoice.edit.col.vat', 'BTW')}</label>
                        <div />
                    </div>
                    {items.map(renderItem)}
                </div>
            </div>
        );
    }

    function renderItem(item: OrderItem) {
        const errors: OrderItemErrors = itemErrors[item.id];
        return (
            <div key={item.id} className='row'>
                <label>{item.ean}</label>
                <BolmateInput
                    value={item.reference}
                    onChange={onChangeItem(item, 'reference')}
                    disabled={!canEditItems}
                />
                <BolmateInput
                    value={item.title}
                    onChange={onChangeItem(item, 'title')}
                    error={errors.title}
                    onFocus={onResetItemError(item.id, 'title')}
                    disabled={!canEditItems}
                />
                <BolmateNumberInput
                    value={item.piecePriceString}
                    onChange={onChangeItem(item, 'piece_price_string')}
                    error={errors.price}
                    onFocus={onResetItemError(item.id, 'price')}
                    disabled={!canEditItems}
                    asCurrency
                />
                <BolmateNumberInput
                    value={item.quantity.toString()}
                    onChange={onChangeItem(item, 'quantity')}
                    error={errors.quantity}
                    onFocus={onResetItemError(item.id, 'quantity')}
                    disabled={!canEditItems}
                />
                <BolmateVATSelect
                    items={getVatItems(getCountryCode(invoice.country))}
                    value={item.vat}
                    onChange={onChangeVAT(item)}
                    disabled={!canEditItems}
                />
            </div>
        );
    }

    return (
        <div className='edit-invoice'>
            <Loader active={loading} />
            <div className='footer-container'>
                <BolmateButton
                    onClick={onCancel}
                    label={t('invoice.details.back', 'Terug')}
                    secondary
                    disabled={loading}
                />
                <BolmateButton onClick={onSubmit} label={t('invoice.edit.save', 'Opslaan')} disabled={loading} />
            </div>
            {renderInvoice()}
            {renderItems()}
        </div>
    );
}
